import { pricePointType, PromoCodeInfo, SubscriptionTags } from '../types';

type NextPlanData = {
  plan: string | null;
  price: string | null;
  originalPrice: string | null;
  promoCode: string | null;
};

type NextPlanDataArgs = {
  pricePoints: pricePointType[];
  tags: string[];
  pricePointId: number;
  pricePointVersionId: number;
  promoCodeInfo?: PromoCodeInfo | null;
};

const applyDiscount = (
  basePrice: number,
  discountPercentage: number | null | undefined,
  discountValue: string | null | undefined
): number => {
  if (discountPercentage) {
    return basePrice * (1 - discountPercentage / 100);
  }
  if (discountValue) {
    return Math.max(0, basePrice - parseFloat(discountValue));
  }
  return basePrice;
};

export const getNextPlanData = ({
  pricePoints,
  tags,
  pricePointId,
  pricePointVersionId,
  promoCodeInfo,
}: NextPlanDataArgs): NextPlanData => {
  const isUpcoming = tags?.includes(SubscriptionTags.isUpcoming);

  const pricePointData =
    pricePoints &&
    pricePoints.find(
      (pricePoint) =>
        pricePoint.id == pricePointId &&
        pricePoint.currentVersion == pricePointVersionId
    );

  let nextPlan = null;
  let nextPrice = null;
  let originalPrice = null;
  let promoCode = null;

  if (pricePointData) {
    if (pricePointData.nextPeriodValue && pricePointData.nextPeriodType) {
      nextPlan = `${pricePointData.nextPeriodValue} ${pricePointData.nextPeriodType}`;
    }
    if (pricePointData.nextBasePrice && pricePointData.currencyCode) {
      let finalPrice = pricePointData.nextBasePrice;

      // Apply promo code discount for next period (non-intro)
      // Only apply if promo code is eligible for next period (based on maximum_number_of_renewals)
      if (
        promoCodeInfo &&
        promoCodeInfo.isEligibleForNextPeriod &&
        !isUpcoming
      ) {
        const discountedPrice = applyDiscount(
          pricePointData.nextBasePrice,
          promoCodeInfo.nextDiscountPercentage,
          promoCodeInfo.nextDiscountValue
        );

        if (discountedPrice !== pricePointData.nextBasePrice) {
          originalPrice = `${pricePointData.nextBasePrice} ${pricePointData.currencyCode}`;
          finalPrice = Math.round(discountedPrice * 100) / 100; // Round to 2 decimal places
          promoCode = promoCodeInfo.code;
        }
      }

      nextPrice = `${finalPrice} ${pricePointData.currencyCode}`;
    }

    if (isUpcoming) {
      const ending =
        pricePointData.introPeriodValue && pricePointData.introPeriodValue > 1
          ? 's'
          : '';

      if (pricePointData.introPeriodValue && pricePointData.introPeriodType) {
        nextPlan = `${pricePointData.introPeriodValue} ${pricePointData.introPeriodType}${ending}`;
      }

      if (pricePointData.introBasePrice && pricePointData.currencyCode) {
        let finalPrice = pricePointData.introBasePrice;

        // Apply promo code discount for intro period
        // Intro period is the first period, so it's always eligible if promo code exists
        if (promoCodeInfo) {
          const discountedPrice = applyDiscount(
            pricePointData.introBasePrice,
            promoCodeInfo.introDiscountPercentage,
            promoCodeInfo.introDiscountValue
          );

          if (discountedPrice !== pricePointData.introBasePrice) {
            originalPrice = `${pricePointData.introBasePrice} ${pricePointData.currencyCode}`;
            finalPrice = Math.round(discountedPrice * 100) / 100;
            promoCode = promoCodeInfo.code;
          }
        }

        nextPrice = `${finalPrice} ${pricePointData.currencyCode}`;
      }
    }
  }
  return { plan: nextPlan, price: nextPrice, originalPrice, promoCode };
};
